import posthog from "posthog-js";

interface ErrorContext {
  component?: string;
  action?: string;
  metadata?: Record<string, unknown>;
}

const MAX_PENDING_ERRORS = 50;
const pendingErrors: Array<{ error: Error; context?: ErrorContext }> = [];

function sendError(error: Error, context?: ErrorContext) {
  posthog.captureException(error, {
    component: context?.component,
    action: context?.action,
    ...context?.metadata,
  });
}

/**
 * Capture an error to PostHog with additional context
 */
export function captureError(
  error: Error,
  context?: ErrorContext
) {
  try {
    // Log to console in development
    if (import.meta.env.DEV) {
      console.error("[Error Tracking]", error, context);
    }

    // Check if PostHog is initialized
    if (!posthog.__loaded) {
      console.warn("[Error Tracking] PostHog not loaded yet, queuing error");
      if (pendingErrors.length === MAX_PENDING_ERRORS) {
        pendingErrors.shift();
      }
      pendingErrors.push({ error, context });
      return;
    }

    sendError(error, context);
  } catch (e) {
    // Silently fail if PostHog is not available
    console.error("Failed to capture error to PostHog:", e);
  }
}

export function flushPendingErrors() {
  if (!posthog.__loaded) return;

  const errors = pendingErrors.splice(0);
  for (const { error, context } of errors) {
    try {
      sendError(error, context);
    } catch (e) {
      console.error("Failed to capture queued error to PostHog:", e);
    }
  }
}

/**
 * Capture a custom error event to PostHog
 */
export function captureErrorEvent(
  eventName: string,
  properties?: Record<string, unknown>
) {
  try {
    posthog.capture(eventName, {
      error: true,
      ...properties,
    });
  } catch (e) {
    console.error("Failed to capture error event to PostHog:", e);
  }
}

/**
 * Set up global error handlers to capture unhandled errors
 */
export function setupGlobalErrorHandlers() {
  flushPendingErrors();

  // Capture unhandled errors
  window.addEventListener("error", (event) => {
    captureError(event.error || new Error(event.message), {
      component: "Global",
      action: "unhandled_error",
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  // Capture unhandled promise rejections
  window.addEventListener("unhandledrejection", (event) => {
    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason));

    captureError(error, {
      component: "Global",
      action: "unhandled_promise_rejection",
      metadata: {
        reason: event.reason,
      },
    });
  });

  // Capture service worker errors
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("error", (event) => {
      captureErrorEvent("service_worker_error", {
        message: event.message || "Service worker error",
      });
    });

    // Listen for error messages from service worker
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event.data?.type === "SW_ERROR") {
        const { error } = event.data;
        const swError = new Error(error.message || "Service worker error");
        if (error.stack) {
          swError.stack = error.stack;
        }

        captureError(swError, {
          component: "ServiceWorker",
          action: error.context || "unknown",
          metadata: {
            context: error.context,
          },
        });
      }
    });
  }

  if (import.meta.env.DEV) {
    console.log("[Error Tracking] Global error handlers set up");
  }
}
